import { z } from "zod";
import { api } from "@/lib/server";
import { recordTrackedEvent } from "@/lib/kiosk-session-tracking";

export const paymentSchema = z.object({
  kioskPaymentNonceKey: z.string(),
  machineId: z.coerce.number(),
  price: z.coerce.number(),
  originalPrice: z.coerce.number(),
  dryerBundleId: z.coerce.number().optional(),
  /** Set when the nth-purchase-campaign discount is applied instead of a coupon; older cash-agents omit it. */
  nthPurchaseCampaignDiscount: z.number().nullish(),
  /** The purchase's order, saved once apply-payment answers; drives the wash start guide. */
  orderId: z.string().nullish(),
});

export type PaymentType = z.infer<typeof paymentSchema>;
const BALANCE_TOPUP_PAYMENT_KEY = "balance-topup-payment-key";

export async function createPayment(
  machineId: number,
  dryerBundleId?: number,
  couponKey?: string,
) {
  const res = await api
    .createPayment({ machineId, dryerBundleId, couponKey })
    .catch((err) => {
      sessionStorage.removeItem("payment");
      throw err;
    });

  sessionStorage.setItem("payment", JSON.stringify(res));
  void recordTrackedEvent({
    eventType: "payment_created",
    machineId,
    dryerBundleId,
    kioskPaymentNonceKey: res.kioskPaymentNonceKey,
    payload: {
      couponKey,
      price: res.price,
      originalPrice: res.originalPrice,
    },
  });
  return res;
}

export function getPaymentFromSessionStorage() {
  const paymentJSON = sessionStorage.getItem(`payment`);

  if (!paymentJSON) {
    return null;
  }

  try {
    return paymentSchema.parse(JSON.parse(paymentJSON));
  } catch (e) {
    console.error(e);
    return null;
  }
}

export function savePaymentOrderId(payment: PaymentType, orderId: string) {
  sessionStorage.setItem(
    "payment",
    JSON.stringify({ ...payment, orderId } satisfies PaymentType),
  );
}

export function clearPaymentFromSessionStorage() {
  sessionStorage.removeItem("payment");
}

export function saveBalanceTopupPaymentKey(paymentKey: string) {
  sessionStorage.setItem(BALANCE_TOPUP_PAYMENT_KEY, paymentKey);
}

export function getBalanceTopupPaymentKey() {
  return sessionStorage.getItem(BALANCE_TOPUP_PAYMENT_KEY);
}

export function clearBalanceTopupPaymentKey() {
  sessionStorage.removeItem(BALANCE_TOPUP_PAYMENT_KEY);
}
